import { Disclosure } from '@headlessui/react';
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CloudUploadIcon,
} from '@heroicons/react/outline';
import { uploadFile } from 'api/v2/upload';
import analysingFilesAnimation from 'assets/Analysing-files-Web.json';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import FileExtensionBadge from 'components/atoms/FileExtensionBadge.component';
import { Modal } from 'components/atoms/Modal';
import ExclaimationTriangleIcon from 'components/atoms/SVG/ExclaimationTrinagleIcon';
import FileUploadIcon from 'components/atoms/SVG/FileUploadIcon.component';
import UseEffect from 'components/atoms/UseEffect';
import FileUploadedItemV2 from 'components/molecules/FileUploadedList/FileUploadedItemV2.component';
import ModalHeading from 'components/molecules/ModalHeading';
import FileUploader from 'components/organisms/FileUploader';
import { AnimatePresence } from 'motion/react';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useGetBankingOnboardingStatus from 'hooks/Bank/useGetBankingOnboardingStatus';
import useInitiateAccountableBankingOnboarding from 'hooks/Bank/useInitiateAccountableBankingOnboarding';
import { useIntercom } from 'hooks/intercom.hook';
import { motion } from 'motion/react';
import { useCallback, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import {
  WEBAPP_BANKING_ONBOARDING_AIDOCSCANNING_CLICKEDCONFIRMDOCISCORRECT,
  WEBAPP_BANKING_ONBOARDING_AIDOCSCANNING_CLICKEDCONTACTCOACHES,
  WEBAPP_BANKING_ONBOARDING_AIDOCSCANNING_CLICKEDTRYANOTHERDOC,
  WEBAPP_BANKING_ONBOARDING_AIDOCSCANNING_SUCCESS_CLICKEDNEXT,
} from 'utils/amplitude/events/bank.amplitude';
import {
  fadeTransition,
  globalyAcceptedFileUploaderTypes,
} from 'utils/constants';
import { getFileExtensionFromPath, getFileNameFromPath } from 'utils/helpers';
import { ExclamationCircleIcon } from 'utils/icons';
import { useAiMetadata } from 'contexts/AIMetadataProvider';
import { useShouldShowTaxAdvisor } from 'hooks/TaxAdvisor/useShouldShowTaxAdvisor';
import { LocalFileUploader } from 'components/organisms/FileUploader/FileUploader.component';
import { FileUploadState } from 'hooks/filesUploadStoreFactory';

const FinanzamtDocUploader: FC<{
  onUploaded?: (file: { url: string; payload: string }) => void;
  onConfirm?: () => void;
  onSkip?: () => void;
  defaultFiles?: { url: string; payload: string }[];
  isConfirming?: boolean;
  onboardingId?: string;
  files?: { url: string; payload: string }[];
  setFiles?: (files: { url: string; payload: string }[]) => void;
}> = ({
  onUploaded,
  defaultFiles,
  onConfirm,
  isConfirming,
  onSkip,
  onboardingId,
  files,
  setFiles,
}) => {
  const { t } = useTranslation();

  const bankingOnboardingAPI = useGetBankingOnboardingStatus();

  const user = useGetCurrentCustomerData().data;

  const numberOfFiles = files?.length || 0;

  const isGewerbetreibender = user?.accountType?.includes('gewerbetreibender');

  const renderUploadedFileItem = useCallback(
    (props: FileUploadState) => (
      <FileUploadedItemV2
        name={props.file.name}
        size={props.file.size}
        status={props.status}
        onRetry={() => null}
        showProgress
        progress={props.progress}
      />
    ),
    [],
  );

  const [currentStep, setCurrentStep] = useState(0);

  const onboardingAPI = useInitiateAccountableBankingOnboarding();

  const { wholeDocumentIssues = [], documentIssues } =
    onboardingAPI?.data || {};

  const shouldShowTaxAdvisor = useShouldShowTaxAdvisor();

  const { showIntercom } = useIntercom();
  const { setIsHelperOpen } = useAiMetadata();

  const openChat = useCallback(() => {
    if (shouldShowTaxAdvisor) {
      setIsHelperOpen(true);
    } else {
      showIntercom();
    }
  }, [setIsHelperOpen, shouldShowTaxAdvisor, showIntercom]);

  const steps = [
    <motion.div
      {...fadeTransition}
      animate={{ opacity: 1, transition: { delay: 0.25 } }}
      className="flex items-center justify-center flex-col relative pb-5 w-full"
      key="1"
    >
      <ModalHeading
        Icon={FileUploadIcon}
        title={t('accountable_banking.onboarding.finanzamt_doc.title.de')}
        subtitle={
          isGewerbetreibender
            ? t(
                'accountable_banking.onboarding.finanzamt_doc.subtitle.gewerbetreibender',
              )
            : t(
                'accountable_banking.onboarding.finanzamt_doc.subtitle.freiberufler',
              )
        }
        titleClassName="w-[80%]"
        subtitleClassName="w-[80%]"
      />

      <Modal
        name="proof of registration example image"
        width="5xl"
        trigger={(api) => (
          <Button
            structure="text"
            className="!underline !text-base !ring-0 !outline-none !font-normal mb-3"
            color="primary"
            onClick={api.openModal}
          >
            {t(
              'accountable_banking.onboarding.finanzamt_doc.see_example_file.cta',
            )}
          </Button>
        )}
        render={() => (
          <img
            src={
              isGewerbetreibender
                ? '/proof_registration_example.png'
                : '/proof_registration_example_freelancer.png'
            }
            alt="proof of registration example image"
          />
        )}
      />

      <div className="w-[80%]">
        <LocalFileUploader
          asDefault
          className="flex-col lg:flex-col w-full !gap-1"
          onUpload={(file: any) => {
            return uploadFile(file).then((details: any) => {
              onUploaded?.({ url: details.key, payload: details.payload });
            });
          }}
          filesListContainerClassName="!px-0"
          dropzoneProps={{
            className:
              'flex-none lg:flex-1 !border-none !bg-transparent h-[230px]',
            renderWrapperClassName: '!p-0 !px-0 !py-0',
            color: 'bank',
            dropOptions: {
              accept: globalyAcceptedFileUploaderTypes,
            },
            render: () => (
              <div className="w-full h-[250px] overflow-hidden text-white group bg-cover bg-center relative flex justify-center bg-white border-dashed border-2 border-taxes-100 font-normal rounded-xl opacity-100 transition-all hover:bg-taxes-25 my-4 mb-0">
                <div className="px-2 overflow-hidden text-center flex flex-col items-center flex-1 justify-center w-full duration-300 pointer-events-none top-0 bottom-0 left-0 right-0">
                  <CloudUploadIcon className="w-5 text-primary-700 mb-6" />

                  <p className="text-primary font-semibold">
                    {t(
                      'accountable_banking.onboarding.finanzamt_doc.uploader.title.de',
                    )}
                  </p>

                  <p className="text-primary-700 text-sm mt-1">
                    {t(
                      'accountable_banking.onboarding.finanzamt_doc.uploader.subtitle.de',
                    )}
                  </p>

                  <span className="text-primary-700 font-semibold px-3 py-1 border border-taxes-100 rounded-lg mt-5">
                    {t(
                      'accountable_banking.onboarding.finanzamt_doc.uploader.cta.de',
                    )}
                  </span>
                </div>
              </div>
            ),
          }}
          renderUploadedFileItem={renderUploadedFileItem}
          filesListProps={{
            renderHeader: () => <div className="mb-4"></div>,
          }}
        />

        <div className="w-full">
          <Button
            className="mt-10 w-full capitalize"
            disabled={!defaultFiles?.length || isConfirming}
            onClick={() => setCurrentStep((step) => step + 1)}
            loading={isConfirming}
          >
            {t('next')}
          </Button>

          <Button
            structure="text"
            className="mt-6 w-full capitalize"
            onClick={() => {
              bankingOnboardingAPI.refetch();

              onSkip?.();
            }}
          >
            {t('accountable_banking.onboarding.finanzamt_doc.skip.cta')}
          </Button>
        </div>
      </div>
    </motion.div>,

    <motion.div {...fadeTransition} animate={{ opacity: 1 }} key="2">
      <UseEffect
        effect={() => {
          onboardingAPI.mutate({
            _id: onboardingId,
            filepaths: files?.map((file) => file.url),
            skipDocumentScan: false,
          });
        }}
        render={() => {
          if (onboardingAPI.isLoading) {
            return (
              <div className="flex flex-col items-center justify-center gap-y-2 h-full">
                <h1 className="relative !capitalize font-bold text-xl flex !bg-clip-text ai-gradient">
                  {t(
                    'accountable_banking.onboarding.analyzing_documents.title',
                  )}
                </h1>

                <p className="text-center text-sm text-primary-600">
                  {t(
                    'accountable_banking.onboarding.analyzing_documents.subtitle',
                  )}
                </p>

                <Lottie
                  options={{
                    animationData: analysingFilesAnimation,
                    loop: true,
                    autoplay: true,
                  }}
                  height={200}
                  width={200}
                />
              </div>
            );
          }

          if (wholeDocumentIssues?.length || documentIssues?.length) {
            const wholeDocumentIssuesLength = wholeDocumentIssues?.length || 0;
            const documentIssuesLength = documentIssues?.length || 0;
            return (
              <div>
                <ModalHeading
                  Icon={ExclaimationTriangleIcon}
                  title={t(
                    'accountable_banking.onboarding.analyzing_documents.issues.title',
                    {
                      numOfIssues:
                        wholeDocumentIssuesLength + documentIssuesLength,
                    },
                  )}
                  subtitle={
                    numberOfFiles === 1
                      ? t(
                          'accountable_banking.onboarding.analyzing_document.issues.subtitle',
                        )
                      : t(
                          'accountable_banking.onboarding.analyzing_documents.issues.subtitle',
                        )
                  }
                  color="red"
                />
                {wholeDocumentIssues?.length ? (
                  <div className="mb-6 border-b border-taxes-100 pb-3">
                    <h2
                      className="self-center text-2xl font-bold text-primary-800 mx-auto mb-3"
                      style={{ hyphens: 'none' }}
                    >
                      {t(
                        'accountable_banking.onboarding.finanzamt_doc.uploader.document_issues',
                      )}
                    </h2>
                    {wholeDocumentIssues.map((issue, index) => {
                      return (
                        <Disclosure key={`${issue} ${index}`}>
                          {({ open }) => (
                            <section className="my-3">
                              <Disclosure.Button className="w-full">
                                <div
                                  className={classNames(
                                    'flex items-center justify-between flex-1 w-full rounded-tr-lg rounded-tl-lg border border-taxes-100 px-5 py-4',
                                    { 'rounded-b-lg': !open },
                                  )}
                                >
                                  <div className="flex items-center gap-x-2">
                                    <ExclamationCircleIcon className="w-5 text-red-500" />

                                    <h2 className="font-semibold">
                                      {t(issue)}
                                    </h2>
                                  </div>

                                  {open ? (
                                    <ChevronUpIcon className="w-5 text-primary-400" />
                                  ) : (
                                    <ChevronDownIcon className="w-5 text-primary-400" />
                                  )}
                                </div>
                              </Disclosure.Button>

                              <AnimatePresence>
                                {open && (
                                  <Disclosure.Panel
                                    static
                                    className="text-gray-500 origin-top py-5 rounded-bl-lg rounded-br-lg border border-taxes-100 px-5 border-t-0"
                                    as={motion.div}
                                  >
                                    {t(`${issue}.details`)}
                                  </Disclosure.Panel>
                                )}
                              </AnimatePresence>
                            </section>
                          )}
                        </Disclosure>
                      );
                    })}
                  </div>
                ) : null}
                {documentIssues?.length ? (
                  <div className="mb-6 border-b border-taxes-100 pb-3">
                    <h2
                      className="self-center text-2xl font-bold text-primary-800 mx-auto mb-3"
                      style={{ hyphens: 'none' }}
                    >
                      {t(
                        'accountable_banking.onboarding.finanzamt_doc.uploader.page_issues',
                      )}
                    </h2>
                    <div className="flex flex-col gap-y-2">
                      {documentIssues.map(({ filepath, issues }) => (
                        <section key={filepath}>
                          <div className="w-full">
                            <div className="flex items-center justify-between flex-1 w-full rounded-tr-lg rounded-tl-lg border border-taxes-100 px-5 py-4">
                              <div className="flex items-center gap-x-2">
                                <FileExtensionBadge
                                  extension={getFileExtensionFromPath(filepath)}
                                />
                                <h2 className="font-semibold">
                                  {getFileNameFromPath(filepath)}
                                </h2>
                              </div>
                            </div>
                          </div>
                          <div
                            key={filepath}
                            className="text-gray-500 origin-top py-5 rounded-bl-lg rounded-br-lg border border-taxes-100 px-5 border-t-0"
                          >
                            <div className="space-y-2">
                              {issues.map((issue, index) => {
                                return (
                                  <Disclosure key={`${filepath}-${index}`}>
                                    {({ open }) => (
                                      <section>
                                        <Disclosure.Button className="w-full">
                                          <div
                                            className={classNames(
                                              'flex items-center justify-between flex-1 w-full rounded-tr-lg rounded-tl-lg border border-taxes-100 px-5 py-4',
                                              { 'rounded-b-lg': !open },
                                            )}
                                          >
                                            <div className="flex items-center gap-x-2">
                                              <ExclamationCircleIcon className="w-5 text-red-500" />

                                              <h2 className="font-semibold">
                                                {t(issue)}
                                              </h2>
                                            </div>

                                            {open ? (
                                              <ChevronUpIcon className="w-5 text-primary-400" />
                                            ) : (
                                              <ChevronDownIcon className="w-5 text-primary-400" />
                                            )}
                                          </div>
                                        </Disclosure.Button>

                                        <AnimatePresence>
                                          {open && (
                                            <Disclosure.Panel
                                              static
                                              className="text-gray-500 origin-top py-5 rounded-bl-lg rounded-br-lg border border-taxes-100 px-5 border-t-0"
                                              as={motion.div}
                                            >
                                              {t(`${issue}.details`)}
                                            </Disclosure.Panel>
                                          )}
                                        </AnimatePresence>
                                      </section>
                                    )}
                                  </Disclosure>
                                );
                              })}
                            </div>
                          </div>
                        </section>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="flex justify-center items-center gap-x-2">
                  <Button
                    structure="secondary"
                    className="mt-6 w-full"
                    onClick={openChat}
                    tracingEvent={
                      WEBAPP_BANKING_ONBOARDING_AIDOCSCANNING_CLICKEDCONTACTCOACHES
                    }
                  >
                    {t('contact_one_of_our_coaches')}
                  </Button>

                  <Button
                    className="mt-6 w-full"
                    onClick={() => {
                      setFiles?.([]);
                      setCurrentStep((step) => step - 1);
                    }}
                    tracingEvent={
                      WEBAPP_BANKING_ONBOARDING_AIDOCSCANNING_CLICKEDTRYANOTHERDOC
                    }
                  >
                    {t('try_another_doc')}
                  </Button>
                </div>

                <Button
                  structure="text"
                  className="mt-4 w-full"
                  onClick={onConfirm}
                  tracingEvent={
                    WEBAPP_BANKING_ONBOARDING_AIDOCSCANNING_CLICKEDCONFIRMDOCISCORRECT
                  }
                >
                  {t(
                    'accountable_banking.onboarding.analyzing_document.skip_doc_scanning',
                  )}
                </Button>
              </div>
            );
          }

          return (
            <div>
              <ModalHeading
                Icon={CheckIcon}
                title={t(
                  'accountable_banking.onboarding.analyzing_documents.success.title',
                )}
                subtitle={t(
                  'accountable_banking.onboarding.analyzing_documents.success.subtitle',
                )}
                color="green"
              />

              {files?.map(({ url }) => (
                <div
                  key={url}
                  className="border border-primary-100 p-4 my-3 rounded-lg flex items-center gap-x-2"
                >
                  <FileExtensionBadge
                    extension={getFileExtensionFromPath(url)}
                  />

                  <span className="text-primary font-semibold">
                    {getFileNameFromPath(url)}
                  </span>
                </div>
              ))}

              <div className="flex justify-center">
                <Button
                  className="mt-6 w-full capitalize"
                  onClick={onConfirm}
                  tracingEvent={
                    WEBAPP_BANKING_ONBOARDING_AIDOCSCANNING_SUCCESS_CLICKEDNEXT
                  }
                >
                  {t('next')}
                </Button>
              </div>
            </div>
          );
        }}
        deps={[user?.country, files, onboardingId]}
      />
    </motion.div>,
  ];

  return <AnimatePresence mode="wait">{steps[currentStep]}</AnimatePresence>;
};

export default FinanzamtDocUploader;
